import React, { useCallback, useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import SpeedDialFab from "../../../components/ui/SpeedDialFab";
import { ModalShell } from "../../../components/ui/ModalFrame";
import { MODAL_LAYER } from "../../../constants/modalLayers";
import { MODAL_DIALOG_ACTIONS_END } from "../../../constants/layoutSpacing";
import { cn } from "../../../utils/cn";

const GenerateContractButton = ({
  isFormValid,
  generationStatus,
  onGenerate,
  onSaveDraft,
  onBackToTemplate,
  requiredFieldsCount,
  filledFieldsCount,
  showSaveDraft = true,
  bypassConfirmModal = false,
  generateButtonLabel = "Gerar Documento PDF",
  readyStatusLabel = "Pronto para gerar",
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isBusy = generationStatus !== "idle";
  const isGenerating = generationStatus === "generating";
  const showBackToTemplate = typeof onBackToTemplate === "function";
  const statusLabel = isFormValid ? readyStatusLabel : "Campos pendentes";
  const statusDotClassName = cn(
    "w-3 h-3 rounded-full flex-shrink-0 animate-pulse",
    isFormValid ? "bg-success" : "bg-warning",
  );
  const fieldCountLabel = `${filledFieldsCount} de ${requiredFieldsCount} campos obrigatórios preenchidos`;

  const getButtonText = () => {
    if (generationStatus === "generating") return "Gerando Documento...";
    if (generationStatus === "processing") return "Processando...";
    if (!isFormValid) {
      return `Preencha todos os campos (${filledFieldsCount}/${requiredFieldsCount})`;
    }
    return generateButtonLabel;
  };

  const handleGenerateClick = useCallback(() => {
    if (!isFormValid || isBusy) return;
    if (bypassConfirmModal) {
      onGenerate();
      return;
    }
    setIsConfirmOpen(true);
  }, [bypassConfirmModal, isBusy, isFormValid, onGenerate]);

  const handleConfirmGenerate = () => {
    setIsConfirmOpen(false);
    onGenerate();
  };

  const speedDialActions = useMemo(() => {
    const actions = [];

    if (showBackToTemplate) {
      actions.push({
        id: "back",
        label: "Editar Modelo",
        iconName: "ArrowLeft",
        disabled: isBusy,
        onClick: onBackToTemplate,
      });
    }

    if (showSaveDraft) {
      actions.push({
        id: "draft",
        label: "Salvar Rascunho",
        iconName: "Save",
        disabled: isBusy,
        onClick: onSaveDraft,
      });
    }

    actions.push({
      id: "generate",
      label: isGenerating
        ? "Gerando…"
        : isFormValid
          ? generateButtonLabel
          : "Campos pendentes",
      iconName: "FileText",
      variant: "primary",
      disabled: !isFormValid || isBusy,
      onClick: handleGenerateClick,
    });

    return actions;
  }, [
    generateButtonLabel,
    handleGenerateClick,
    isBusy,
    isFormValid,
    isGenerating,
    onBackToTemplate,
    onSaveDraft,
    showBackToTemplate,
    showSaveDraft,
  ]);

  const actionButtons = (
    <>
      {showBackToTemplate && (
        <Button
          variant="outline"
          size="lg"
          iconName="ArrowLeft"
          iconPosition="left"
          disabled={isBusy}
          onClick={onBackToTemplate}
          className="w-full sm:w-auto min-w-0"
        >
          Editar Modelo
        </Button>
      )}

      {showSaveDraft && (
        <Button
          variant="outline"
          size="lg"
          iconName="Save"
          iconPosition="left"
          disabled={isBusy}
          onClick={onSaveDraft}
          className="w-full sm:w-auto min-w-0"
        >
          Salvar Rascunho
        </Button>
      )}

      <Button
        variant="default"
        size="lg"
        iconName="FileText"
        iconPosition="left"
        disabled={!isFormValid || isBusy}
        loading={isGenerating}
        onClick={handleGenerateClick}
        className="w-full sm:w-auto min-w-0"
      >
        {getButtonText()}
      </Button>
    </>
  );

  return (
    <>
      <div className="shrink-0 z-10 bg-card border-t border-border p-4 md:p-6 shadow-elevation-lg overflow-x-hidden">
        <div className="md:hidden flex items-center gap-3 min-w-0 pr-16">
          <div className={statusDotClassName} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {statusLabel}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {fieldCountLabel}
            </p>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-between gap-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
            <div className={statusDotClassName} />
            <div className="flex flex-col min-w-0">
              <span className="text-base font-medium text-foreground truncate">
                {statusLabel}
              </span>
              <span className="text-sm text-muted-foreground">
                {fieldCountLabel}
              </span>
            </div>
          </div>

          <div className="flex flex-row flex-nowrap gap-3 shrink-0">
            {actionButtons}
          </div>
        </div>
      </div>

      <SpeedDialFab ariaLabel="Ações do formulário" actions={speedDialActions} />

      {isConfirmOpen && (
        <ModalShell
          zClass={MODAL_LAYER.standard}
          backdropClassName="bg-black/50"
          panelClassName="bg-card rounded-lg max-w-md w-full p-6 shadow-elevation-lg"
        >
          <h3 className="text-lg font-semibold">
            Atenção antes de gerar o documento
          </h3>

          <p className="text-sm text-muted-foreground mt-2">
            Este modelo de documento é uma sugestão automatizada e{" "}
            <strong>não substitui assessoria jurídica.</strong>
          </p>

          <div className={cn(MODAL_DIALOG_ACTIONS_END, "mt-6")}>
            <Button variant="outline" onClick={() => setIsConfirmOpen(false)}>
              Cancelar
            </Button>

            <Button variant="default" onClick={handleConfirmGenerate}>
              Confirmar e Gerar
            </Button>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default GenerateContractButton;
